import { read as xlsxRead, utils as xlsxUtils } from 'xlsx';

/** Minimal shape produced by CSV/Excel parsing — a structural subset of wiz-leads.ts's `LeadRecord`. */
export interface CsvLeadRow {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  jobTitle: string;
  linkedinUrl?: string;
  industry?: string;
}

/** Column name aliases → CsvLeadRow field mapping */
export const CSV_COLUMN_MAP: Record<string, keyof CsvLeadRow> = {
  first_name: 'firstName',
  firstname: 'firstName',
  'first name': 'firstName',
  vorname: 'firstName',
  last_name: 'lastName',
  lastname: 'lastName',
  'last name': 'lastName',
  nachname: 'lastName',
  email: 'email',
  e_mail: 'email',
  'e-mail': 'email',
  company: 'company',
  firma: 'company',
  organization: 'company',
  organisation: 'company',
  company_name: 'company',
  job_title: 'jobTitle',
  jobtitle: 'jobTitle',
  title: 'jobTitle',
  'job title': 'jobTitle',
  position: 'jobTitle',
  rolle: 'jobTitle',
  linkedin_url: 'linkedinUrl',
  linkedinurl: 'linkedinUrl',
  linkedin: 'linkedinUrl',
  'linkedin url': 'linkedinUrl',
  linkedin_profile: 'linkedinUrl',
  industry: 'industry',
  branche: 'industry',
};

/**
 * Parse a CSV string into an array of string arrays.
 * Handles quoted fields with commas and newlines inside quotes.
 */
export function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let current = '';
  let inQuotes = false;
  let row: string[] = [];

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',' || ch === ';' || ch === '\t') {
      row.push(current.trim());
      current = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(current.trim());
      current = '';
      if (row.some(cell => cell.length > 0)) rows.push(row);
      row = [];
    } else {
      current += ch;
    }
  }
  // Last field / row
  row.push(current.trim());
  if (row.some(cell => cell.length > 0)) rows.push(row);
  return rows;
}

/** Map raw CSV rows to CsvLeadRow[], auto-detecting columns via CSV_COLUMN_MAP. */
export function csvRowsToLeads(rows: string[][]): { leads: CsvLeadRow[]; errors: string[] } {
  if (rows.length < 2) return { leads: [], errors: ['CSV has no data rows'] };

  const headers = rows[0]!.map(h => h.toLowerCase().replace(/['"]/g, '').trim());
  const fieldMap = new Map<number, keyof CsvLeadRow>();
  for (let i = 0; i < headers.length; i++) {
    const mapped = CSV_COLUMN_MAP[headers[i]!];
    if (mapped) fieldMap.set(i, mapped);
  }

  const errors: string[] = [];
  const mappedFields = new Set(fieldMap.values());
  // A lead needs a way to be contacted: at least an "email" OR a "linkedin_url"
  // column must be present (a lead may carry only one of the two).
  if (!mappedFields.has('email') && !mappedFields.has('linkedinUrl')) {
    errors.push(`Need at least an "email" or "linkedin_url" column. Detected columns: ${headers.join(', ')}`);
  }
  if (errors.length > 0) return { leads: [], errors };

  const leads: CsvLeadRow[] = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r]!;
    const record: Record<string, string> = {};
    for (const [colIdx, field] of fieldMap) {
      record[field] = row[colIdx] ?? '';
    }
    // A lead is usable with either an email or a LinkedIn URL — skip only rows
    // that have neither.
    if (!record.email && !record.linkedinUrl) continue;
    leads.push({
      firstName: record.firstName ?? '',
      lastName: record.lastName ?? '',
      email: record.email ?? '',
      company: record.company ?? '',
      jobTitle: record.jobTitle ?? '',
      linkedinUrl: record.linkedinUrl || undefined,
      industry: record.industry || undefined,
    });
  }

  if (leads.length === 0) {
    errors.push('No valid leads found in CSV (every row needs at least an email or a LinkedIn URL)');
  }
  return { leads, errors };
}

/** Read a File as text (UTF-8). */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

/** Read a File as ArrayBuffer. */
export function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

/** Parse an Excel file (.xlsx/.xls) into the same string[][] format as parseCsvText. */
export function parseExcelFile(buffer: ArrayBuffer): string[][] {
  const workbook = xlsxRead(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const sheet = workbook.Sheets[sheetName]!;
  const rows: string[][] = xlsxUtils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
  return rows;
}

/** Parse a CSV/TSV/Excel File straight into lead rows. */
export async function parseLeadFile(file: File): Promise<{ leads: CsvLeadRow[]; errors: string[] }> {
  const name = file.name.toLowerCase();
  const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls');
  const isCsv = name.endsWith('.csv') || name.endsWith('.tsv') || name.endsWith('.txt');
  if (!isCsv && !isExcel) {
    return { leads: [], errors: ['Unsupported file type. Please upload a .csv or .xlsx file.'] };
  }
  const rows = isExcel ? parseExcelFile(await readFileAsArrayBuffer(file)) : parseCsvText(await readFileAsText(file));
  return csvRowsToLeads(rows);
}
